import { useEffect, useState, useCallback } from 'react'
import { GetSurvey, SaveSurveyResults } from '../../../services/SurveyService'
import { type UploadFilesEvent, type Model, type Question, type QuestionPanelDynamicModel } from 'survey-core'
import { registerCustomQuestions } from './questions'
import { SurveyDisplay } from './SurveyDisplay'
import { SurveyNavigationButtons } from './SurveyNavigationButtons'
import { type SurveyType } from '../../../models/SurveySchema'
import { UploadFile } from '../../../services/FileService'
import { Box, Typography } from '@mui/material'
import { SurveyStepper } from './SurveyStepper'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { SURVEY_COMMENT_SUFFIX, SURVEY_FILE_KEY_SUFFIX, SURVEY_FILE_SUFFIX } from '../../../constants/surveyConstants'

registerCustomQuestions()

// Constants for metadata field suffixes
const AUTO_SAVE_DELAY = 500

interface QuestionWithMetadata extends Question {
  uploadedFileName?: string
  uploadedFileKey?: string
  panels?: Array<{
    questions: QuestionWithMetadata[]
  }>
}

function isPanelDynamicQuestion (question: Question): question is QuestionPanelDynamicModel {
  return question.getType() === 'paneldynamic' || question.getType() === 'surveypaneldynamic'
}

function hasMetadata (question: Question): question is QuestionWithMetadata {
  return 'uploadedFileName' in question || 'uploadedFileKey' in question
}

interface Props {
  annualPlanId: number
  surveyType: SurveyType
  title?: string
  navigateToSummary?: () => void
  planOrgId: number
}

export function STARTSurveyDisplay ({
  annualPlanId,
  surveyType,
  title,
  navigateToSummary,
  planOrgId
}: Props): JSX.Element {
  const [surveyJson, setSurveyJson] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [answers, setAnswers] = useState<Record<string, any> | undefined>(undefined)
  const [surveyModel, setSurveyModel] = useState<Model | null>(null)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [pageCompleteStatus, setPageCompleteStatus] = useState<boolean[]>([])
  const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([0]))

  const extractQuestionMetadata = (question: QuestionWithMetadata, questionName: string, data: Record<string, any>): void => {
    if (question.extraInfo !== undefined) {
      data[questionName + SURVEY_COMMENT_SUFFIX] = (question.extraInfo != null && question.extraInfo !== '') ? question.extraInfo : null
    }
    if (question.uploadedFileName !== undefined) {
      data[questionName + SURVEY_FILE_SUFFIX] = (question.uploadedFileName != null && question.uploadedFileName !== '') ? question.uploadedFileName : null
    }
    if (question.uploadedFileKey !== undefined) {
      data[questionName + SURVEY_FILE_KEY_SUFFIX] = (question.uploadedFileKey != null && question.uploadedFileKey !== '') ? question.uploadedFileKey : null
    }
  }

  const saveSurvey = useCallback(async (survey: Model, submit?: boolean): Promise<void> => {
    const data = survey.data
    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          panel.questions.forEach((panelQuestion) => {
            extractQuestionMetadata(panelQuestion, panelQuestion.name, data[question.name][index])
          })
        })
      }

      if (hasMetadata(q)) {
        extractQuestionMetadata(question, q.name, data)
      }
    })

    await SaveSurveyResults({
      schemaId: surveyType,
      surveyAnswers: JSON.stringify(data),
      planOrgId,
      submit
    })
    if (submit === true) {
      navigateToSummary?.()
    }
  }, [annualPlanId, surveyType, navigateToSummary, planOrgId])

  const loadQuestionMetadata = useCallback((question: QuestionWithMetadata, questionName: string, savedData: Record<string, any>, survey: Model): void => {
    if (savedData[questionName + SURVEY_COMMENT_SUFFIX] != null) {
      question.extraInfo = savedData[questionName + SURVEY_COMMENT_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_SUFFIX] != null) {
      question.uploadedFileName = savedData[questionName + SURVEY_FILE_SUFFIX]
    }
    if (savedData[questionName + SURVEY_FILE_KEY_SUFFIX] != null) {
      question.uploadedFileKey = savedData[questionName + SURVEY_FILE_KEY_SUFFIX]

      let saveTimeout: NodeJS.Timeout
      question.onPropertyChanged.add((_sender: any, _options: any) => {
        clearTimeout(saveTimeout)
        saveTimeout = setTimeout(() => {
          void saveSurvey(survey)
        }, AUTO_SAVE_DELAY)
      })
    }
  }, [saveSurvey])

  const onComplete = useCallback(async (survey: Model, _options: any): Promise<void> => {
    await saveSurvey(survey, true)
  }, [saveSurvey])

  const onLoad = useCallback(async (survey: Model): Promise<void> => {
    setSurveyModel(survey)

    survey.getAllQuestions().forEach(q => {
      const question = q as QuestionWithMetadata
      if (answers == null) return

      if (isPanelDynamicQuestion(q) && question.panels !== undefined) {
        question.panels.forEach((panel, index) => {
          if (answers[question.name]?.[index] == null) return

          panel.questions.forEach((panelQuestion) => {
            const savedData = answers[question.name][index]
            loadQuestionMetadata(panelQuestion, panelQuestion.name, savedData, survey)
          })
        })
      }

      if (hasMetadata(q)) {
        loadQuestionMetadata(question, question.name, answers, survey)
      }
    })

    survey.onUploadFiles.add((_sender: Model, { question, files, callback }: UploadFilesEvent) => {
      void Promise.all(files.map(async (file: File) => {
        try {
          const key = await UploadFile(file)
          const q = question as QuestionWithMetadata
          q.uploadedFileKey = key
          q.uploadedFileName = file.name
          return {
            file,
            content: key
          }
        } catch (e) {
          console.error('Failed to upload file', e)
          return undefined
        }
      })).then((results) => {
        const success = results.filter(r => r !== undefined)
        callback(success)
      })
    })
  }, [answers, saveSurvey, loadQuestionMetadata])

  useEffect(() => {
    void loadSchema()
  }, [])

  useEffect(() => {
    if (surveyModel == null) return

    const updatePage = (): void => {
      setCurrentPageIndex(surveyModel.currentPageNo)
    }

    surveyModel.onCurrentPageChanged.add(updatePage)
    return () => {
      surveyModel.onCurrentPageChanged.remove(updatePage)
    }
  }, [surveyModel])

  useEffect(() => {
    if (surveyModel == null) return

    const checkPageStatus = (): void => {
      const statuses = surveyModel.pages.map((page, index) => {
        if (!visitedPages.has(index)) {
          return false
        }

        const allQuestions = page.getQuestions(true)
        let isComplete = true
        for (let i = 0; i < allQuestions.length; i++) {
          const q = allQuestions[i]
          const isActuallyRequired = q.isRequired && q.isVisible
          if (isActuallyRequired && q.isEmpty()) {
            isComplete = false
            break
          }
        }
        return isComplete
      })
      setPageCompleteStatus(statuses)
    }

    const markPageVisited = (): void => {
      setVisitedPages(prev => new Set(prev).add(surveyModel.currentPageNo))
    }

    checkPageStatus()
    markPageVisited()
    surveyModel.onValueChanged.add(checkPageStatus)
    surveyModel.onCurrentPageChanged.add(checkPageStatus)
    surveyModel.onCurrentPageChanged.add(markPageVisited)
    return () => {
      surveyModel.onValueChanged.remove(checkPageStatus)
      surveyModel.onCurrentPageChanged.remove(checkPageStatus)
      surveyModel.onCurrentPageChanged.remove(markPageVisited)
    }
  }, [surveyModel, visitedPages])

  const loadSchema = async (): Promise<void> => {
    const survey = await handleApiResponse(GetSurvey(surveyType, planOrgId))
    if (survey != null) {
      const schemaJson = JSON.parse(survey.content)
      setSurveyJson(schemaJson)
      if (survey.answers != null) setAnswers(JSON.parse(survey.answers))
    }
    setLoading(false)
  }

  if (loading) return <div>Loading...</div>

  const showStepper = surveyJson?.pages?.length > 1

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mt: 2, ml: 5 }}>
        <Typography variant="h3">{title}</Typography>
      </Box>
      {showStepper && surveyModel != null && (
        <SurveyStepper
          surveyModel={surveyModel}
          currentPageIndex={currentPageIndex}
          pageCompleteStatus={pageCompleteStatus}
        />
      )}
      {surveyJson !== null
        ? (
          <SurveyDisplay
            surveyJson={surveyJson}
            onComplete={onComplete}
            answers={answers}
            onLoad={onLoad}
            saveSurvey={saveSurvey}
          />
          )
        : <div>No schema found</div>
        }
      {surveyModel != null && (
        <SurveyNavigationButtons
          survey={surveyModel}
          saveSurvey={saveSurvey}
        />
      )}
    </Box>
  )
}
